// HistoryPanel — S2: história záznamu (kto / čo / kedy) — ústava 12.0 bod 1
// ════════════════════════════════════════════════════════════════════
// Dohľadateľnosť: KAŽDÝ záznam v Matrixe musí mať viditeľnú históriu zmien.
// Číta z tabuľky audit_log cez GET /api/audit-log?module=&entity_id=.
// Audit sa zapisuje automaticky (server.js middleware pri POST/PUT/DELETE/PATCH).
//
// POUŽITIE — v detaile záznamu, typicky ako tab „História":
//   <HistoryPanel module="work_schedule" entityId={record.id} />

import React, { useState, useEffect, useCallback } from 'react'
import { COLORS, TYPO, SPACE, RADIUS, TINT } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'

const ACTION_CFG = {
  create: { label: 'Vytvorenie', icon: '+', color: COLORS.add },
  edit:   { label: 'Úprava',     icon: '', color: COLORS.nav },
  delete: { label: 'Zmazanie',   icon: '×', color: COLORS.remove },
  action: { label: 'Akcia',      icon: '•', color: COLORS.muted },
}

function relTime(iso) {
  if (!iso) return ''
  const d = new Date(String(iso).replace(' ', 'T'))
  if (isNaN(d.getTime())) return String(iso)
  const sec = (Date.now() - d.getTime()) / 1000
  if (sec < 60)    return 'pred chvíľou'
  if (sec < 3600)  return `pred ${Math.floor(sec / 60)} min`
  if (sec < 86400) return `pred ${Math.floor(sec / 3600)} h`
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export function HistoryPanel({
  module,
  entityId,
  limit = 40,
  emptyText = 'Zatiaľ žiadne záznamy histórie.',
}) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    if (!module || !entityId) { setRows([]); setLoading(false); return }
    setLoading(true); setErr(null)
    try {
      const qs = new URLSearchParams({
        module: String(module),
        entity_id: String(entityId),
        limit: String(limit),
      })
      const r = await fetchAuth(`/api/audit-log?${qs.toString()}`)
      const d = await r.json()
      setRows(Array.isArray(d) ? d : [])
    } catch (_) {
      setErr('História sa nenačítala.')
    } finally {
      setLoading(false)
    }
  }, [module, entityId, limit])

  useEffect(() => { load() }, [load])

  return (
    <div style={{
      background: COLORS.bg,
      border: `1px solid ${COLORS.line}`,
      borderRadius: RADIUS.lg,
      overflow: 'hidden',
    }}>
      {/* hlavička */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: SPACE.sm,
        padding: `${SPACE.sm}px ${SPACE.md}px`,
        borderBottom: `1px solid ${COLORS.line}`,
        background: TINT.nav,
      }}>
        <span style={{
          fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
          color: COLORS.nav, textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>História — kto / čo / kedy</span>
        <div style={{ flex: 1 }} />
        <button
          onClick={load}
          title="Obnoviť"
          style={{
            background: 'transparent', border: `1px solid ${COLORS.line}`,
            borderRadius: RADIUS.sm, cursor: 'pointer',
            fontSize: TYPO.sizes.xs, color: COLORS.muted, padding: '2px 8px',
            fontFamily: 'inherit',
          }}
        >↻ Obnoviť</button>
      </div>

      {/* telo */}
      <div style={{ maxHeight: 360, overflow: 'auto' }}>
        {loading ? (
          <div style={{ padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
            Načítavam…
          </div>
        ) : err ? (
          <div style={{ padding: SPACE.lg, color: COLORS.remove, fontSize: TYPO.sizes.sm }}>
            {err}
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
            {emptyText}
          </div>
        ) : (
          rows.map((row) => {
            const cfg = ACTION_CFG[row.action] || ACTION_CFG.action
            return (
              <div key={row.id} style={{
                display: 'flex', gap: SPACE.sm,
                padding: `${SPACE.sm}px ${SPACE.md}px`,
                borderBottom: `1px solid ${COLORS.lineSoft}`,
              }}>
                {/* bod akcie */}
                <div style={{
                  flexShrink: 0, width: 22, height: 22,
                  borderRadius: RADIUS.full,
                  background: cfg.color, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold,
                }}>{cfg.icon}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: TYPO.sizes.sm }}>
                    <span style={{ fontWeight: TYPO.weights.semibold, color: cfg.color }}>
                      {cfg.label}
                    </span>
                    <span style={{ color: COLORS.muted }}> — </span>
                    <span style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text }}>
                      {row.username || '?'}
                    </span>
                  </div>
                  {row.detail && (
                    <div style={{
                      fontSize: TYPO.sizes.xs, color: COLORS.muted,
                      fontFamily: TYPO.mono, marginTop: 2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{row.detail}</div>
                  )}
                </div>

                <div style={{
                  flexShrink: 0, fontSize: TYPO.sizes.xs, color: COLORS.muted,
                  textAlign: 'right', whiteSpace: 'nowrap',
                }} title={row.created_at || ''}>
                  {relTime(row.created_at)}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default HistoryPanel
